import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useField } from 'react-form';
import { toInt } from '../../../utils/utils';

import FormGroup from './FormGroup';
import TextField from './TextField';

const TextFieldGroup = ({
  FormGroupProps = null,
  InputProps = {},
  inputProps: callerInputProps = {},
  characterLimit = undefined,
  'data-tour-id': dataTourId = null,
  debouncingSafetyAssured = false,
  defaultValue = undefined,
  disabled = false,
  field,
  helperText: helperTextProp,
  includeTitleInErrorMessage = true,
  // only here to make sure inline isn't part of textFieldProps
  inline: _excludeInline = undefined,
  inputRef = null,
  isRequiredTitleLabel = false,
  maxValue = null,
  maxValueNotice = null,
  multiline = false,
  onTextChange = null,
  placeholder = null,
  preventNegativeValues = false,
  showSteppers = false,
  size,
  step = null,
  style = null,
  title = null,
  titleTooltip = null,
  triggerValidationOnInit = false,
  type = 'text',
  validate = null,
  variant = 'outlined',
  ...textFieldProps
}) => {
  const initValue = useMemo(() => defaultValue, [defaultValue]);
  const { value, setValue, meta: { error } } = useField(field, { defaultIsTouched: triggerValidationOnInit, defaultValue: initValue, validate });

  const onChange = useCallback((e) => {
    const val = e.target.value;
    if (preventNegativeValues && (val < 0 || Number.isNaN(val))) return;

    setValue(val);
    if (onTextChange) onTextChange(val);
  }, [onTextChange, preventNegativeValues, setValue]);
  const onIncrementDown = useCallback(() => { setValue(toInt(value) - 1); }, [setValue, value]);
  const onIncrementUp = useCallback(() => { setValue(toInt(value) + 1); }, [setValue, value]);

  // Character length warning
  let helperText = null;
  if (characterLimit) {
    helperText = `${(value || '').length}/${characterLimit} characters`;
  }
  if (helperTextProp) {
    if (typeof helperTextProp === 'function') {
      helperText = helperTextProp(error);
    } else {
      helperText = helperTextProp;
    }
  }

  let helperTextId;
  if (helperText) {
    helperTextId = `${field}-helper-text`;
  }

  // Format validation error
  const errorTitle = title || placeholder;
  let errorMessage = error;
  let errorId;
  if (errorMessage && errorTitle && includeTitleInErrorMessage) {
    errorId = `${field}-error`;
    if (typeof errorTitle === 'string') {
      errorMessage = `${errorTitle} ${errorMessage.toLowerCase()}`;
    } else {
      errorMessage = (
        <React.Fragment>
          {errorTitle} {errorMessage.toLowerCase()}
        </React.Fragment>
      );
    }
  }
  let noticeMessage;
  if (!errorMessage && maxValue && maxValue === value) {
    noticeMessage = maxValueNotice;
  }

  return (
    <FormGroup error={errorMessage} notice={noticeMessage} errorId={errorId} {...FormGroupProps}>
      <TextField
        characterLimit={characterLimit}
        debouncingSafetyAssured={debouncingSafetyAssured}
        disabled={disabled}
        error={!!errorMessage}
        field={field}
        helperText={helperText}
        id={field}
        inputRef={inputRef}
        isRequiredTitleLabel={isRequiredTitleLabel}
        maxValue={maxValue}
        multiline={multiline}
        onChange={onChange}
        onIncrementDown={(preventNegativeValues && value <= 0) ? null : onIncrementDown}
        onIncrementUp={(maxValue != null && value >= maxValue) ? null : onIncrementUp}
        placeholder={placeholder}
        showSteppers={showSteppers}
        size={size || 'medium'}
        step={step}
        style={style}
        title={title}
        titleTooltip={titleTooltip}
        type={type}
        value={value === null || value === undefined ? '' : String(value)}
        variant={variant}
        InputProps={{
          'aria-describedby': [errorId, helperTextId, InputProps?.['aria-describedby']].filter(Boolean).join(' ') || undefined,
          'data-tour-id': dataTourId,
          ...InputProps,
        }}
        inputProps={{
          'aria-invalid': errorMessage ? 'true' : undefined,
          ...callerInputProps,
        }}
        {...textFieldProps}
      />
    </FormGroup>
  );
};

TextFieldGroup.propTypes = {
  characterLimit: PropTypes.number,
  'data-tour-id': PropTypes.string,
  debouncingSafetyAssured: PropTypes.bool,
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  field: PropTypes.string.isRequired,
  FormGroupProps: PropTypes.object,
  includeTitleInErrorMessage: PropTypes.bool,
  InputProps: PropTypes.object,
  inputProps: PropTypes.object,
  inputRef: PropTypes.object,
  isRequiredTitleLabel: PropTypes.bool,
  maxValue: PropTypes.number,
  maxValueNotice: PropTypes.string,
  multiline: PropTypes.bool,
  onTextChange: PropTypes.func,
  placeholder: PropTypes.string,
  preventNegativeValues: PropTypes.bool,
  showSteppers: PropTypes.bool,
  step: PropTypes.string,
  style: PropTypes.object,
  title: PropTypes.node,
  titleTooltip: PropTypes.string,
  triggerValidationOnInit: PropTypes.bool,
  type: PropTypes.string,
  validate: PropTypes.func,
  variant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
};

export default TextFieldGroup;
